import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { merge, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TranslationService } from 'src/app/modules/i18n';
import { ProjectWorkflowTask } from '../models/project-management.model';
import {
  ActiveProjectWorkflowService,
  PROJECT_WORKFLOW_TASK_CONTEXT_KEY,
  workflowPathsMatch,
  WorkflowTaskContext,
} from '../services/active-project-workflow.service';
import { ProjectManagementService } from '../services/project-management.service';
import { GorevPanosuPermissionService } from '../../dashboard-task/gorev-panosu-permission.service';
import { locale as english } from '../i18n/en';
import { locale as turkish } from '../i18n/tr';

@Component({
    selector: 'app-workflow-url-task-float',
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        TranslateModule,
        NgbTooltipModule,
    ],
    templateUrl: './workflow-url-task-float.component.html',
    styleUrls: ['./workflow-url-task-float.component.scss']
})
export class WorkflowUrlTaskFloatComponent implements OnInit, OnDestroy {
  private readonly injector = inject(Injector);

  matchedTasks: ProjectWorkflowTask[] = [];
  matchedTask: ProjectWorkflowTask | null = null;
  activeTaskIndex = 0;
  visible = false;
  selectedStatus = 'Pending';
  statusOptions: string[] = ['Pending', 'InProgress', 'Completed'];
  saving = false;

  dragX = 0;
  dragY = 0;
  private dragActive = false;
  private lastClientX = 0;
  private lastClientY = 0;

  private userDismissed = false;
  private readonly projectCompletionById = new Map<string, boolean>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private workflow: ActiveProjectWorkflowService,
    private projectManagementService: ProjectManagementService,
    private toastr: ToastrService,
    private translationService: TranslationService,
  ) {
    this.translationService.loadTranslations(english, turkish);
  }

  ngOnInit(): void {
    this.workflow.ensureHydrated();

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.userDismissed = false;
        this.runSync();
      });

    merge(this.workflow.tasks$, this.workflow.activeProjectIds$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.runSync());

    this.runSync();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  startDrag(event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    event.preventDefault();
    this.dragActive = true;
    this.lastClientX = event.clientX;
    this.lastClientY = event.clientY;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.dragActive) {
      return;
    }
    this.dragX += event.clientX - this.lastClientX;
    this.dragY += event.clientY - this.lastClientY;
    this.lastClientX = event.clientX;
    this.lastClientY = event.clientY;
    this.cdr.markForCheck();
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.dragActive = false;
  }

  dismiss(event: Event): void {
    event.stopPropagation();
    this.userDismissed = true;
    this.visible = false;
    this.cdr.markForCheck();
  }

  isCompleted(): boolean {
    return (
      String(this.matchedTask?.status ?? '').toLowerCase() === 'completed'
    );
  }

  isTaskCompleted(task: ProjectWorkflowTask | null | undefined): boolean {
    return String(task?.status ?? '').toLowerCase() === 'completed';
  }

  isMatchedProjectCompleted(): boolean {
    const projectId = this.matchedTask?.projectId;
    if (!projectId) {
      return false;
    }
    return this.projectCompletionById.get(projectId) === true;
  }

  selectTaskTab(index: number): void {
    if (index < 0 || index >= this.matchedTasks.length) {
      return;
    }
    this.activeTaskIndex = index;
    this.matchedTask = this.matchedTasks[index] ?? null;
    if (this.matchedTask) {
      this.statusOptions = this.taskStatusOptionsFor(this.matchedTask);
      this.selectedStatus = this.matchedTask.status || 'Pending';
    }
    this.cdr.markForCheck();
  }

  taskStatusLabel(status: string): string {
    const map: Record<string, string> = {
      Completed: 'PROJECT_MANAGEMENT.TASK_STATUS_COMPLETED',
      InProgress: 'PROJECT_MANAGEMENT.TASK_STATUS_IN_PROGRESS',
      Pending: 'PROJECT_MANAGEMENT.TASK_STATUS_PENDING',
    };
    const key = map[status];
    return key ? this.translationService.instant(key) : status;
  }

  /** Proje yönetimi görev kartlarındaki select ile aynı renkler */
  taskStatusSelectClass(status: string): Record<string, boolean> {
    const n = this.normalizeTaskStatus(status);
    return {
      'pm-task-inline-select--pending': n === 'pending',
      'pm-task-inline-select--inprogress': n === 'inprogress',
      'pm-task-inline-select--completed': n === 'completed',
    };
  }

  taskStatusOptionStyle(status: string): Record<string, string> {
    const n = this.normalizeTaskStatus(status);
    if (n === 'completed') {
      return {
        color: '#18864f',
        backgroundColor: '#e8fff3',
      };
    }
    if (n === 'inprogress') {
      return {
        color: '#1b64d1',
        backgroundColor: '#eef6ff',
      };
    }
    return {
      color: '#b4234a',
      backgroundColor: '#ffeef3',
    };
  }

  applyStatus(): void {
    if (!this.matchedTask || this.saving) {
      return;
    }
    if (this.isMatchedProjectCompleted()) {
      this.notifyProjectCompletedTaskLock();
      return;
    }
    const next = this.selectedStatus;
    if (!next || next === this.matchedTask.status) {
      return;
    }
    this.pushStatus(next);
  }

  markCompleted(): void {
    if (!this.matchedTask || this.saving || this.isCompleted()) {
      return;
    }
    if (this.isMatchedProjectCompleted()) {
      this.notifyProjectCompletedTaskLock();
      return;
    }
    this.selectedStatus = 'Completed';
    this.pushStatus('Completed');
  }

  private pushStatus(status: string): void {
    const task = this.matchedTask;
    if (!task) {
      return;
    }
    if (
      !this.injector
        .get(GorevPanosuPermissionService)
        .assertProjeYonetimiWrite()
    ) {
      return;
    }
    if (this.isMatchedProjectCompleted()) {
      this.notifyProjectCompletedTaskLock();
      return;
    }
    this.saving = true;
    this.projectManagementService
      .updateTaskStatus({
        projectId: task.projectId,
        taskId: task.id,
        status,
      })
      .subscribe({
        next: () => {
          this.workflow.patchTaskStatusLocal(task.id, status);
          this.patchSessionContextStatus(status);
          this.toastr.success(
            this.translationService.instant(
              'PROJECT_MANAGEMENT.TASK_STATUS_SAVED',
            ),
          );
          this.saving = false;
          if (status.toLowerCase() === 'completed') {
            this.focusNextOpenTask(task.id);
          }
          void this.router.navigate(['/project-management', task.projectId], {
            queryParams: { phase: 'tasks' },
          });
          this.runSync();
        },
        error: () => {
          this.saving = false;
          this.toastr.error(
            this.translationService.instant(
              'PROJECT_MANAGEMENT.UPDATE_TASK_FAILED',
            ),
          );
          this.cdr.markForCheck();
        },
      });
  }

  private patchSessionContextStatus(status: string): void {
    const raw = sessionStorage.getItem(PROJECT_WORKFLOW_TASK_CONTEXT_KEY);
    if (!raw) {
      return;
    }
    try {
      const ctx = JSON.parse(raw) as WorkflowTaskContext;
      if (ctx.contextKind !== 'url' || ctx.taskId !== this.matchedTask?.id) {
        return;
      }
      ctx.status = status;
      sessionStorage.setItem(
        PROJECT_WORKFLOW_TASK_CONTEXT_KEY,
        JSON.stringify(ctx),
      );
    } catch {
      /* ignore */
    }
  }

  private isLoggedIn(): boolean {
    try {
      const u = JSON.parse(localStorage.getItem('currentUser') || '{}') as {
        token?: string;
      };
      return !!(u && u.token);
    } catch {
      return false;
    }
  }

  private isAuthRoute(url: string): boolean {
    const p = url.split('?')[0].toLowerCase();
    return p.startsWith('/auth');
  }

  private runSync(): void {
    if (!this.isLoggedIn() || this.isAuthRoute(this.router.url)) {
      this.visible = false;
      this.matchedTasks = [];
      this.matchedTask = null;
      this.cdr.markForCheck();
      return;
    }

    if (this.userDismissed) {
      this.visible = false;
      this.matchedTasks = [];
      this.matchedTask = null;
      this.cdr.markForCheck();
      return;
    }

    const path = this.router.url;
    const tasks = this.resolveMatchedTasks(path);
    const visibleTasks = tasks.filter(
      (t) => !this.isProjectIdCompleted(t.projectId),
    );
    this.matchedTasks = visibleTasks;
    if (!visibleTasks.length) {
      this.matchedTask = null;
      this.activeTaskIndex = 0;
      this.visible = false;
      this.cdr.markForCheck();
      tasks.forEach((t) => this.ensureProjectCompletionState(t.projectId));
      return;
    }
    if (this.activeTaskIndex >= visibleTasks.length) {
      this.activeTaskIndex = 0;
    }
    const task = visibleTasks[this.activeTaskIndex];
    this.matchedTask = task;
    this.visible = !!task;

    if (task) {
      this.statusOptions = this.taskStatusOptionsFor(task);
      this.selectedStatus = task.status || 'Pending';
      this.ensureProjectCompletionState(task.projectId);
    }

    this.cdr.markForCheck();
  }

  private taskStatusOptionsFor(task: ProjectWorkflowTask): string[] {
    const base = ['Pending', 'InProgress', 'Completed'];
    const s = task.status;
    if (s && !base.includes(s)) {
      return [s, ...base];
    }
    return base;
  }

  private normalizeTaskStatus(
    status: string | null | undefined,
  ): 'pending' | 'inprogress' | 'completed' {
    const s = String(status ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
    if (s === 'completed') {
      return 'completed';
    }
    if (s === 'inprogress') {
      return 'inprogress';
    }
    return 'pending';
  }

  private resolveMatchedTasks(routerUrl: string): ProjectWorkflowTask[] {
    const tasks = this.workflow.tasks$.value;
    const matchedByPath = tasks.filter((t) => {
      const p = ActiveProjectWorkflowService.resolveInternalTaskUrl(t);
      return !!p && workflowPathsMatch(routerUrl, p);
    });

    try {
      const raw = sessionStorage.getItem(PROJECT_WORKFLOW_TASK_CONTEXT_KEY);
      if (raw) {
        const ctx = JSON.parse(raw) as WorkflowTaskContext;
        if (ctx.contextKind === 'url' && ctx.targetUrl) {
          if (workflowPathsMatch(routerUrl, ctx.targetUrl)) {
            const t = matchedByPath.find((x) => x.id === ctx.taskId);
            if (t) {
              return [t, ...matchedByPath.filter((x) => x.id !== t.id)];
            }
          }
        }
      }
    } catch {
      /* ignore */
    }

    return matchedByPath;
  }

  private focusNextOpenTask(completedTaskId: string): void {
    if (!this.matchedTasks.length) {
      return;
    }
    const currentIndex = this.matchedTasks.findIndex((t) => t.id === completedTaskId);
    if (currentIndex < 0) {
      return;
    }
    for (let offset = 1; offset <= this.matchedTasks.length; offset += 1) {
      const nextIndex = (currentIndex + offset) % this.matchedTasks.length;
      const candidate = this.matchedTasks[nextIndex];
      if (!this.isTaskCompleted(candidate)) {
        this.activeTaskIndex = nextIndex;
        return;
      }
    }
  }

  private ensureProjectCompletionState(projectId: string | null | undefined): void {
    const id = String(projectId ?? '').trim();
    if (!id || this.projectCompletionById.has(id)) {
      return;
    }
    this.projectManagementService.getProjectById(id).subscribe({
      next: (detail) => {
        const normalized = String((detail as any)?.status ?? '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '');
        const completed = normalized === 'completed' || normalized === 'complete';
        this.projectCompletionById.set(id, completed);
        if (completed) {
          this.runSync();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.projectCompletionById.set(id, false);
      },
    });
  }

  private isProjectIdCompleted(projectId: string | null | undefined): boolean {
    const id = String(projectId ?? '').trim();
    if (!id) {
      return false;
    }
    return this.projectCompletionById.get(id) === true;
  }

  private notifyProjectCompletedTaskLock(): void {
    this.toastr.info(
      this.translationService.instant(
        'PROJECT_MANAGEMENT.PROJECT_COMPLETED_TASK_STATUS_LOCKED',
      ),
    );
  }
}
